import React from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import i18n from '../i18n';

/**
 * Keeps the dashboard shell (sidebar, header, logout) alive when a page
 * throws during render. Without this, any child exception whitescreens
 * the entire app and the only recovery is logging out.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Page render failed:', error, info);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  // A crash right after a deploy usually means this tab is still running the
  // old bundle, so "Try again" does a full reload instead of re-rendering it.
  handleRetry = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const title = i18n.t('common.pageCrashTitle');
    const help = i18n.t('common.pageCrashHelp');
    const tryAgain = i18n.t('common.tryAgain');
    const goHome = i18n.t('common.goHome');

    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }} role="alert" data-demo-target="page-render-error">
        <Paper sx={{ p: { xs: 2, sm: 4 }, maxWidth: 560, mx: 'auto', textAlign: 'center' }}>
          <Typography variant="h5" component="h2" sx={{ mb: 1, fontWeight: 600, color: '#000062' }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {help}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="center"
          >
            <Button
              variant="contained"
              onClick={this.handleRetry}
              aria-label={tryAgain}
            >
              {tryAgain}
            </Button>
            {typeof this.props.onGoHome === 'function' && (
              <Button
                variant="outlined"
                onClick={() => {
                  this.setState({ hasError: false });
                  this.props.onGoHome();
                }}
                aria-label={goHome}
              >
                {goHome}
              </Button>
            )}
          </Stack>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
